/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Button, Typography2 } from '@coursera/cds-core';
import { ClosedCaptioningIcon } from '@coursera/cds-icons';

import { getHeadingStyle } from 'bundles/unified-description-page-common/components/modules/product-overview/details/Details';
import LanguagesDetailItemModal from 'bundles/unified-description-page-common/components/modules/product-overview/details/LanguagesDetailItemModal';
import { useModal } from 'bundles/unified-description-page-common/components/shared/modals/Modal';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';
import { getLanguageString } from 'bundles/unified-description-page-common/utils/languages';

import _t from 'i18n!nls/xdp';

type Props = {
  primaryLanguages: string[];
  subtitleLanguages?: string[] | null;
};

const styles = {
  itemTitle: css`
    padding: var(--cds-spacing-100) 0;
  `,
  icon: css`
    display: block;
  `,
  button: css`
    padding: 0;
    margin-left: 2px;

    /* set vertical-align to unset to vertically center the button within the "Subtitles" line */
    vertical-align: unset;

    :hover {
      background: transparent;
    }
  `,
};

const LanguagesDetailItem = ({ primaryLanguages, subtitleLanguages }: Props) => {
  const { enableXDPRedesignPhase1, enableXDPRedesignDescriptionRework } = useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || enableXDPRedesignDescriptionRework;
  const { isModalOpen, setModalOpen } = useModal();

  let cta;

  if (subtitleLanguages?.length === 1) {
    cta = (
      <FormattedMessage
        message={_t(`Subtitles: {subtitleLanguage}`)}
        subtitleLanguage={getLanguageString(subtitleLanguages)}
      />
    );
  } else if (subtitleLanguages?.length === 2) {
    cta = (
      <FormattedMessage
        message={_t(`Subtitles: {subtitleLanguage1}, {subtitleLanguage2}`)}
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        subtitleLanguage1={getLanguageString([subtitleLanguages[0]])}
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        subtitleLanguage2={getLanguageString([subtitleLanguages[1]])}
      />
    );
  } else if (subtitleLanguages && subtitleLanguages.length > 2) {
    cta = (
      <FormattedMessage
        message={_t(`Subtitles: {subtitleLanguage1}, {subtitleLanguage2}, {otherLanguages}`)}
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        subtitleLanguage1={getLanguageString([subtitleLanguages[0]])}
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        subtitleLanguage2={getLanguageString([subtitleLanguages[1]])}
        otherLanguages={
          <Button variant="ghost" size="small" edgeAlign="start" css={styles.button} onClick={setModalOpen}>
            {_t('+#{numberOfOtherLanguages} more', { numberOfOtherLanguages: subtitleLanguages.length - 2 })}
          </Button>
        }
      />
    );
  }

  if (!primaryLanguages?.length) {
    return null;
  }

  return (
    <React.Fragment>
      <ClosedCaptioningIcon size="large" css={styles.icon} />
      <Typography2 variant={getHeadingStyle(enableXDPRedesign)} css={styles.itemTitle} component="div">
        {/*
         // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess */}
        {getLanguageString([primaryLanguages[0]])}
      </Typography2>
      {cta && (
        <Typography2 variant="bodySecondary" color="supportText" component="div">
          {cta}
        </Typography2>
      )}
      <LanguagesDetailItemModal
        open={isModalOpen}
        setOpen={setModalOpen}
        primaryLanguages={primaryLanguages}
        subtitleLanguages={subtitleLanguages}
      />
    </React.Fragment>
  );
};

export default LanguagesDetailItem;
